/**
 * Экран калибровки · окно «Калибровка на старте».
 *
 * Показывается ВМЕСТО приложения, пока калибровка не пройдена, — тем же приёмом, что
 * замок окна 3: ни навигации, ни карточек, ни статистики. ⛔ Кнопки «пропустить» нет
 * (решение владельца 19.08): проходят все — и новые, и уже зашедшие.
 *
 * ⛔ Своей логики здесь нет: машина состояний живёт в `src/calibration.ts` и проверяется
 * запуском, а судьбу слов считает ядро. Здесь только разметка и нажатия.
 */

import "../styles/calibration.css";

import * as cal from "../calibration";
import * as api from "../api";

const esc = (s: string): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

function shell(): HTMLElement | null {
  return document.getElementById("shell");
}

function frame(inner: string): string {
  return '<div class="calib"><div class="calib-card">' + inner + "</div></div>";
}

function bar(value: number): string {
  return `<div class="calib-bar"><i style="width:${Math.round(value * 100)}%"></i></div>`;
}

function probeHtml(s: cal.CalState): string {
  const item = cal.current(s);
  if (!item) return "";
  return frame(
    '<p class="calib-h">Подбираем твой уровень</p>' +
    `<p class="calib-sub">${esc(cal.hint(s))}</p>` +
    bar(cal.progress(s)) +
    `<div class="calib-word">${esc(item.word)}</div>` +
    '<div class="calib-btns">' +
      '<button class="calib-no" type="button" data-act="no">Не знаю</button>' +
      '<button class="calib-yes" type="button" data-act="yes">Знаю</button>' +
    "</div>" +
    '<p class="calib-foot">Отвечай честно — от этого зависит, с чего начнётся обучение.</p>',
  );
}

function verifyHtml(s: cal.CalState): string {
  const w = s.verifyQueue[0];
  if (!w) return "";
  const opts = cal.verifyOptions(w);
  return frame(
    '<p class="calib-h">Проверим пару слов</p>' +
    '<p class="calib-sub">Ты отметил это слово знакомым — выбери перевод</p>' +
    bar(cal.progress(s)) +
    `<div class="calib-word">${esc(w.word)}</div>` +
    '<div class="calib-opts">' +
      opts.map((o, i) =>
        `<button class="calib-opt" type="button" data-opt="${i}">${esc(o)}</button>`).join("") +
    "</div>",
  );
}

function ceilingHtml(): string {
  return frame(
    '<p class="calib-h">Частотное ядро тебе мало</p>' +
    cal.ceilingLines().map((l) => `<p class="calib-line">${esc(l)}</p>`).join("") +
    '<button class="calib-yes wide" type="button" data-act="finish">Продолжить</button>',
  );
}

function resultHtml(r: api.CalibrationResult): string {
  return frame(
    `<p class="calib-h">${esc(cal.resultTitle(r as cal.CalResult))}</p>` +
    cal.resultLines(r as cal.CalResult)
      .map((l) => `<p class="calib-line">${esc(l)}</p>`).join("") +
    '<button class="calib-yes wide" type="button" data-act="go">Начать заниматься</button>',
  );
}

function busyHtml(text: string): string {
  return frame(`<p class="calib-h">${esc(text)}</p><div class="calib-dots"></div>`);
}

/**
 * Показать калибровку вместо приложения.
 * @param onDone зовём, когда человек прошёл её до конца — приложение поднимается заново.
 */
export async function showCalibration(onDone: () => Promise<void> | void): Promise<void> {
  const host = shell();
  if (!host) return;
  host.innerHTML = busyHtml("Собираем слова…");

  let data: api.CalibrationOut;
  try {
    data = await api.calibration();
  } catch (e) {
    host.innerHTML = frame(
      '<p class="calib-h">Не открылось</p>' +
      `<p class="calib-line">${esc(api.humanMessage(e, "Связь пропала"))}</p>` +
      '<button class="calib-yes wide" type="button" data-act="retry">Попробовать снова</button>');
    host.querySelector("[data-act]")?.addEventListener("click", () => void showCalibration(onDone));
    return;
  }

  let s = cal.startState(data.rules, data.words);

  const draw = (): void => {
    const h = shell();
    if (!h) return;
    if (s.stage === "probe") h.innerHTML = probeHtml(s);
    else if (s.stage === "verify") h.innerHTML = verifyHtml(s);
    else if (s.stage === "ceiling") h.innerHTML = ceilingHtml();
    else { void finish(); return; }
    wire();
  };

  const finish = async (): Promise<void> => {
    const h = shell();
    if (h) h.innerHTML = busyHtml("Настраиваем словарь…");
    try {
      const res = await api.calibrationApply(cal.payload(s));
      const h2 = shell();
      if (h2) {
        h2.innerHTML = resultHtml(res);
        h2.querySelector("[data-act='go']")?.addEventListener("click", () => void onDone());
      }
    } catch (e) {
      const h2 = shell();
      if (h2) {
        h2.innerHTML = frame(
          '<p class="calib-h">Не получилось настроить</p>' +
          `<p class="calib-line">${esc(api.humanMessage(e, "Связь пропала"))}</p>` +
          '<button class="calib-yes wide" type="button" data-act="again">Ещё раз</button>');
        h2.querySelector("[data-act='again']")?.addEventListener("click", () => void finish());
      }
    }
  };

  function wire(): void {
    const h = shell();
    if (!h) return;
    h.querySelectorAll<HTMLElement>("[data-act]").forEach((b) => {
      b.addEventListener("click", () => {
        const act = b.dataset.act;
        if (act === "yes" || act === "no") { s = cal.answer(s, act === "yes"); draw(); }
        else if (act === "finish") { void finish(); }
      });
    });
    h.querySelectorAll<HTMLElement>("[data-opt]").forEach((b) => {
      b.addEventListener("click", () => {
        const w = s.verifyQueue[0];
        const right = !!w && cal.verifyOptions(w)[Number(b.dataset.opt)] === w.ru;
        s = cal.verifyAnswer(s, right);
        draw();
      });
    });
  }

  draw();
}
